import type { HeartRateEvent } from '@model/DeviceConfiguration';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { FETCH_CONCURRENCY } from '../packages/spectral-workspace/constants';
import {
  getCachedSessionHrDataAnalysis,
  type SessionHrDataAnalysisResult,
} from '../ai-repair';
import type { SpectralAnalysisSnapshot } from '../types';
import {
  loadEffectiveSessionHydration,
  type EffectiveSessionDataSource,
  type ResolvedEffectiveSessionValidation,
} from '../services/resolveEffectiveSessionValidations';
import {
  getEffectiveHeartRateData,
  getRepairedOnlyEvents,
  parseHeartRateData,
} from '../../../utils/hrDataRepair';

export type RecalculatedSessionHydrationState =
  | {
      source: 'repaired' | 'effective';
      validation: NonNullable<ResolvedEffectiveSessionValidation['validation']>;
      excluded?: undefined;
    }
  | {
      source: 'repaired' | 'effective';
      validation?: undefined;
      excluded: NonNullable<ResolvedEffectiveSessionValidation['excluded']>;
    };

export type RepairPreviewHydrationState = {
  repairedOnly: HeartRateEvent[];
  repairedEvents: HeartRateEvent[];
  originalCount: number;
  repairedCount: number;
};

export type SessionHydrationPatch = {
  sessionId: number;
  dataSource: EffectiveSessionDataSource;
  effectiveEvents: HeartRateEvent[];
  recalculated?: RecalculatedSessionHydrationState;
  repairPreview?: RepairPreviewHydrationState;
};

type SessionAnalysisState =
  | { status: 'idle' }
  | {
      status: 'clean';
      summary?: string;
      findings?: string[];
    }
  | {
      status: 'needs_repair';
      summary?: string;
      findings?: string[];
      duplicateCount: number;
      gapCount: number;
    }
  | { status: 'failed'; message: string };

const mapAnalysisResultToState = (
  result: SessionHrDataAnalysisResult
): Exclude<SessionAnalysisState, { status: 'idle' }> => {
  if (result.status === 'failed') {
    return { status: 'failed', message: result.message ?? 'Analisi fallita.' };
  }

  if (result.status === 'clean') {
    return {
      status: 'clean',
      summary: result.summary,
      findings: result.findings,
    };
  }

  return {
    status: 'needs_repair',
    summary: result.summary,
    findings: result.findings,
    duplicateCount: result.duplicateCount ?? 0,
    gapCount: result.gapCount ?? 0,
  };
};

const buildRecalculatedState = (
  resolved: ResolvedEffectiveSessionValidation
): RecalculatedSessionHydrationState | undefined => {
  const source =
    resolved.dataSource === 'hrData_repaired'
      ? ('repaired' as const)
      : ('effective' as const);

  if (resolved.validation) {
    return { source, validation: resolved.validation };
  }

  if (resolved.excluded) {
    return { source, excluded: resolved.excluded };
  }

  return undefined;
};

const buildRepairPreview = (
  configuration: Awaited<
    ReturnType<typeof loadEffectiveSessionHydration>
  >['configuration']
): RepairPreviewHydrationState | undefined => {
  const repairedOnly = getRepairedOnlyEvents(configuration.hrData_repaired);

  if (repairedOnly.length === 0) {
    return undefined;
  }

  const repairedEvents = parseHeartRateData(configuration.hrData_repaired);

  return {
    repairedOnly,
    repairedEvents,
    originalCount: parseHeartRateData(configuration.hrData).length,
    repairedCount: repairedEvents.length,
  };
};

const buildPatchFromHydration = (
  hydration: Awaited<ReturnType<typeof loadEffectiveSessionHydration>>
): SessionHydrationPatch => ({
  sessionId: hydration.sessionId,
  dataSource: hydration.resolved.dataSource,
  effectiveEvents: getEffectiveHeartRateData(hydration.configuration),
  recalculated: buildRecalculatedState(hydration.resolved),
  repairPreview: buildRepairPreview(hydration.configuration),
});

type UseEffectiveSessionHydrationOptions = {
  snapshot: SpectralAnalysisSnapshot | null;
  enabled: boolean;
  prioritySessionId?: number | null;
  onLoadingChange: (loading: boolean) => void;
  onSessionPatch: (patch: SessionHydrationPatch) => void;
  onSeedSessionAnalysis: (
    entries: Record<number, Exclude<SessionAnalysisState, { status: 'idle' }>>
  ) => void;
};

export const useEffectiveSessionHydration = ({
  snapshot,
  enabled,
  prioritySessionId,
  onLoadingChange,
  onSessionPatch,
  onSeedSessionAnalysis,
}: UseEffectiveSessionHydrationOptions) => {
  const queryClient = useQueryClient();
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!snapshot || snapshot.includedSessions.length === 0) {
      return;
    }

    const seeded: Record<
      number,
      Exclude<SessionAnalysisState, { status: 'idle' }>
    > = {};

    snapshot.includedSessions.forEach(item => {
      const cached = getCachedSessionHrDataAnalysis(item.validation.sessionId);
      if (cached) {
        seeded[item.validation.sessionId] = mapAnalysisResultToState(cached);
      }
    });

    if (Object.keys(seeded).length > 0) {
      onSeedSessionAnalysis(seeded);
    }
  }, [onSeedSessionAnalysis, snapshot]);

  useEffect(() => {
    if (!enabled || !snapshot || snapshot.includedSessions.length === 0) {
      onLoadingChange(false);
      return;
    }

    const sessionIds = snapshot.includedSessions.map(
      item => item.validation.sessionId
    );
    const orderedIds = prioritySessionId
      ? [
          prioritySessionId,
          ...sessionIds.filter(sessionId => sessionId !== prioritySessionId),
        ]
      : sessionIds;

    const runId = ++runIdRef.current;
    let cancelled = false;
    onLoadingChange(true);

    const queue = [...orderedIds];
    let activeCount = 0;

    const pump = () => {
      if (cancelled) {
        return;
      }

      while (activeCount < FETCH_CONCURRENCY && queue.length > 0) {
        const sessionId = queue.shift();
        if (sessionId == null) {
          break;
        }

        activeCount += 1;
        void loadEffectiveSessionHydration(queryClient, sessionId)
          .then(hydration => {
            if (cancelled || runId !== runIdRef.current) {
              return;
            }

            onSessionPatch(buildPatchFromHydration(hydration));
          })
          .catch(() => {
            // supplementary live data
          })
          .finally(() => {
            activeCount -= 1;

            if (cancelled || runId !== runIdRef.current) {
              return;
            }

            if (queue.length === 0 && activeCount === 0) {
              onLoadingChange(false);
              return;
            }

            pump();
          });
      }

      if (queue.length === 0 && activeCount === 0) {
        onLoadingChange(false);
      }
    };

    pump();

    return () => {
      cancelled = true;
      runIdRef.current += 1;
      onLoadingChange(false);
    };
  }, [
    enabled,
    onLoadingChange,
    onSessionPatch,
    prioritySessionId,
    queryClient,
    snapshot,
  ]);
};
